import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { cachedGet } from '../lib/apiCache';
import { sanitizeHtml } from '../lib/sanitize';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCategories, useRegions } from '../lib/taxonomy';
import { format } from 'date-fns';
import { Share2, ChevronRight, MapPin, MessageCircle } from 'lucide-react';
import branding from '../config/branding';
import { NewsCard } from '../components/news/NewsCard';
import { toast } from 'sonner';
import { setArticleHeader } from '../lib/articleHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticlePage() {
  const { articleId } = useParams();
  const { t, isHindi } = useLanguage();
  const { token } = useAuth();
  const { label, pathFor } = useCategories();
  const { districtLabel, districtPath } = useRegions();

  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [popularArticles, setPopularArticles] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
    fetchPopular();
  }, [articleId]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      // Logged-in authors and admins can also open their unpublished articles (preview links)
      const response = await axios.get(`${API}/articles/${articleId}`, {
        headers: token && token !== 'google-oauth' ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      setArticle(response.data);

      const [relatedRes, latestRes] = await Promise.all([
        cachedGet(axios, `${API}/public/articles`, { params: { category: response.data.category, limit: 7 } }, 60_000),
        cachedGet(axios, `${API}/public/articles`, { params: { limit: 5 } }, 60_000),
      ]);

      setRelatedArticles(relatedRes.data.filter(a => a.article_id !== articleId));
      setLatestArticles(latestRes.data.filter(a => a.article_id !== articleId));
    } catch (error) {
      console.error('Error fetching article:', error);
      toast.error(isHindi ? 'लेख लोड करने में त्रुटि' : 'Error loading article');
    } finally {
      setLoading(false);
    }
  };

  const fetchPopular = async () => {
    try {
      const res = await cachedGet(axios, `${API}/public/articles`, { params: { limit: 50 } }, 60_000);
      const sorted = [...res.data]
        .sort((a, b) => b.views - a.views)
        .slice(0, 9);
      setPopularArticles(sorted);
    } catch (_) { }
  };

  const formatDate = (dateStr) => {
    try { return format(new Date(dateStr), 'MMM d, yyyy, h:mm a'); }
    catch { return dateStr; }
  };

  const shareUrl = () => `${window.location.origin}/article/${articleId}`;
  const shareTitle = () => (isHindi && article.title_hi ? article.title_hi : article.title);

  // Android WebView has no navigator.share and often blocks the clipboard API, so:
  // 1. the Android app's own share sheet, if it exposes window.UPwalaApp.share(title, url)
  // 2. the browser share sheet  3. clipboard  4. old-style copy that works in WebViews
  const handleShare = async () => {
    const url = shareUrl();
    const title = shareTitle();
    if (window.UPwalaApp && typeof window.UPwalaApp.share === 'function') {
      window.UPwalaApp.share(title, url);
      return;
    }
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch (_) { }
      return;
    }
    let copied = false;
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch (_) {
      const input = document.createElement('textarea');
      input.value = url;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      copied = document.execCommand('copy');
      document.body.removeChild(input);
    }
    if (copied) toast.success(isHindi ? 'लिंक कॉपी हो गया!' : 'Link copied to clipboard!');
    else toast.error(isHindi ? 'लिंक कॉपी नहीं हो सका' : 'Could not copy the link');
  };

  const whatsappHref = () =>
    `https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareTitle()}\n${shareUrl()}`)}`;

  const headlineRef = useRef(null);

  // Tell the slim header which story is open, and whether the headline has scrolled out of view
  useEffect(() => {
    if (!article || loading) return undefined;
    setArticleHeader({
      sectionLabel: label(article.category, isHindi),
      sectionTo: pathFor(article.category),
      title: shareTitle(),
      scrolled: false,
      onShare: handleShare,
      whatsappHref: whatsappHref(),
    });
    let observer;
    const el = headlineRef.current;
    if (el && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(([entry]) => {
        const scrolled = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        setArticleHeader((prev) => (prev && prev.scrolled !== scrolled ? { ...prev, scrolled } : prev));
      }, { rootMargin: '-56px 0px 0px 0px' });
      observer.observe(el);
    }
    return () => {
      observer?.disconnect();
      setArticleHeader(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article, loading, isHindi]);

  /* ── Loading state ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-white max-w-[640px] mx-auto px-4 pt-10 animate-pulse">
        <div className="h-3 bg-gray-200 w-24 mb-4" />
        <div className="h-10 bg-gray-200 mb-3" />
        <div className="h-10 bg-gray-200 w-2/3 mb-6" />
        <div className="aspect-[3/2] bg-gray-200" />
      </div>
    );
  }

  /* ── Not found ── */
  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className={`text-3xl font-bold text-[var(--ink)] mb-6 ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
            {isHindi ? 'लेख नहीं मिला' : 'Article not found'}
          </h2>
          <Link to="/" className="btn-outline">
            {isHindi ? 'होम पर जाएं' : 'Go to Home'}
          </Link>
        </div>
      </div>
    );
  }

  const title = isHindi && article.title_hi ? article.title_hi : article.title;
  const content = isHindi && article.content_hi ? article.content_hi : article.content;
  const categoryName = label(article.category, isHindi);
  const placeName = districtLabel(article.district, isHindi);

  // Create a plain text summary for SEO description
  const plainTextContent = content ? content.replace(/<[^>]+>/g, '') : '';
  const seoDescription = plainTextContent.substring(0, 160) + (plainTextContent.length > 160 ? '...' : '');
  const minutes = Math.max(1, Math.round(plainTextContent.split(/\s+/).filter(Boolean).length / 200));

  const headFont = isHindi ? 'font-hindi-heading' : 'font-heading';
  const font = isHindi ? 'font-hindi' : '';
  const trending = popularArticles.filter((a) => a.article_id !== articleId).slice(0, 8);

  // "Share" pill and round WhatsApp button, shown under the headline and again after the story
  const shareTools = (testIdSuffix = '') => (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleShare}
        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-[var(--rule)] hover:border-[var(--ink)] font-ui text-xs font-semibold text-[var(--ink)] transition-colors"
        data-testid={`share-btn${testIdSuffix}`}
      >
        <Share2 className="w-4 h-4" />
        {isHindi ? 'पूरा लेख शेयर करें' : 'Share full article'}
      </button>
      <a
        href={whatsappHref()}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center w-9 h-9 rounded-full border border-[var(--rule)] hover:border-[var(--ink)] text-[var(--ink)] transition-colors"
        aria-label="WhatsApp"
        data-testid={`whatsapp-share${testIdSuffix}`}
      >
        <MessageCircle className="w-4 h-4" />
      </a>
    </div>
  );

  const SectionHeading = ({ children, to }) => (
    <div className="section-rule mb-5">
      {to ? (
        <Link to={to} className={`group inline-flex items-center gap-0.5 font-ui text-base font-bold text-[var(--ink)] ${font}`}>
          <span className="group-hover:underline underline-offset-4">{children}</span>
          <ChevronRight className="w-4 h-4" />
        </Link>
      ) : (
        <h2 className={`font-ui text-base font-bold text-[var(--ink)] ${font}`}>{children}</h2>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="article-page">
      <Helmet>
        <title>{`${title} | ${isHindi ? branding.nameHi : branding.name}`}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="article" />
        {article.image_url && <meta property="og:image" content={article.image_url} />}
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <article className="px-4 pt-6 sm:pt-10">
        {/* Headline */}
        <header className="max-w-[640px] mx-auto">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
            <Link to={pathFor(article.category)} className={`category-pill ${font}`}>
              {categoryName}
            </Link>
            {article.is_featured && <span className="featured-badge">{t('featured')}</span>}
          </div>
          <h1
            ref={headlineRef}
            className={`text-[2rem] sm:text-[2.5rem] font-bold text-[var(--ink)] leading-[1.12] ${headFont}`}
          >
            {title}
          </h1>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-6 mb-6">
            <span className="story-meta">
              {isHindi ? `${minutes} मिनट में पढ़ें` : `${minutes} min read`}
            </span>
            {shareTools()}
          </div>
        </header>

        {/* Photo */}
        {article.image_url && (
          <figure className="max-w-[640px] mx-auto mb-8">
            <img src={article.image_url} alt={title} className="w-full max-h-[75vh] object-cover bg-[var(--bg-subtle)]" />
            {article.source_name && (
              <figcaption className="max-w-[640px] mx-auto mt-2 font-ui text-xs text-[var(--ink-muted)]">
                {isHindi ? 'स्रोत: ' : 'Source: '}{article.source_name}
              </figcaption>
            )}
          </figure>
        )}

        {/* Byline */}
        <div className="max-w-[640px] mx-auto mb-8 font-ui">
          <Link
            to={`/author/${article.author_id}`}
            className={`text-[15px] font-bold text-[var(--ink)] hover:underline underline-offset-4 ${font}`}
          >
            {t('by')} {article.author_name}
          </Link>
          <p className="flex flex-wrap items-center gap-y-1 text-[13px] text-[var(--ink-soft)] mt-1">
            <span className="whitespace-nowrap">{isHindi ? 'प्रकाशित ' : 'Published '}{formatDate(article.created_at)}</span>
            {placeName && (
              <>
                <span className="mx-2 text-[var(--rule)]">|</span>
                <Link
                  to={districtPath(article.district)}
                  className={`inline-flex items-center gap-1 whitespace-nowrap hover:underline underline-offset-4 ${font}`}
                >
                  <MapPin className="w-3.5 h-3.5" />
                  {placeName}
                </Link>
              </>
            )}
            <span className="mx-2 text-[var(--rule)]">|</span>
            <span className="whitespace-nowrap">{article.views} {t('views')}</span>
          </p>
        </div>

        {/* Body */}
        <div
          className={`article-content max-w-[640px] mx-auto ${font}`}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
        />

        {/* Share again at the end of the story */}
        <div className="max-w-[640px] mx-auto mt-8">
          {shareTools('-bottom')}
        </div>
      </article>

      <div className="max-w-7xl mx-auto px-4 mt-16">
        {/* More in this section + trending */}
        {(relatedArticles.length > 0 || trending.length > 0) && (
          <div className="grid lg:grid-cols-12 gap-x-8 gap-y-12">
            {relatedArticles.length > 0 && (
              <section className={trending.length > 0 ? 'lg:col-span-8' : 'lg:col-span-12'}>
                <SectionHeading to={pathFor(article.category)}>
                  {isHindi ? `${categoryName} से और` : `More in ${categoryName}`}
                </SectionHeading>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-8">
                  {relatedArticles.slice(0, 6).map((a) => (
                    <NewsCard key={a.article_id} article={a} variant="tile" />
                  ))}
                </div>
              </section>
            )}

            {trending.length > 0 && (
              <aside className={relatedArticles.length > 0 ? 'lg:col-span-4' : 'lg:col-span-12'}>
                <SectionHeading>
                  {isHindi ? `${branding.nameHi} में ट्रेंडिंग` : `Trending in ${branding.name}`}
                </SectionHeading>
                <ol>
                  {trending.map((pop) => {
                    const popTitle = isHindi && pop.title_hi ? pop.title_hi : pop.title;
                    return (
                      <li key={pop.article_id} className="story-rule first:border-t-0">
                        <Link to={`/article/${pop.article_id}`} className="group block py-4">
                          <h3 className={`news-headline text-[1.1875rem] font-medium leading-snug text-[var(--ink)] ${headFont}`}>
                            {popTitle}
                          </h3>
                        </Link>
                      </li>
                    );
                  })}
                </ol>
              </aside>
            )}
          </div>
        )}

        {/* Latest News */}
        {latestArticles.length > 0 && (
          <section className="mt-14">
            <SectionHeading>{isHindi ? 'ताज़ा खबरें' : 'Latest News'}</SectionHeading>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-8">
              {latestArticles.slice(0, 4).map((a) => (
                <NewsCard key={a.article_id} article={a} variant="tile" />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
